import React, { useState } from 'react';
import { validateActivityPayload, GAME_MODE_CATALOG, type PollPayload } from '@interactive-room';
import { emptyPollPayload } from '../emptyPayloads';
import { StudioLiveShell } from '../components/StudioLiveShell';
import { createDraftId, saveActivityDraft, type ActivityDraft } from '../activityStorage';
import { resolveStudioLaunchDefaults } from '../studioLaunch';
import { generateSubjectAwareActivityDraft } from '../studioActivityGeneration';
import type { StudioLiveEditorProps } from '../types/editor.types';

const OPTION_TINTS = ['#f5f3ff', '#fdf4ff', '#eff6ff', '#f0fdf4', '#fff7ed', '#fef2f2'];

export function PollLiveEditor({
  draftId,
  embedded,
  onSaved,
  launchSubjectId,
  launchPresetId,
}: StudioLiveEditorProps) {
  const launch = resolveStudioLaunchDefaults(emptyPollPayload, {
    draftId,
    launchSubjectId,
    launchPresetId,
  });
  const [payload, setPayload] = useState<PollPayload>(() => launch.payload);
  const [subjectTag, setSubjectTag] = useState(() => launch.subjectTag);
  const [slideIndex, setSlideIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [savedId] = useState(draftId ?? createDraftId());

  const meta = GAME_MODE_CATALOG.poll;
  const slide = payload.slides[slideIndex];

  const updateSlide = (patch: Record<string, unknown>) => {
    setPayload((prev) => ({
      ...prev,
      slides: prev.slides.map((s, i) => (i === slideIndex ? ({ ...s, ...patch } as PollPayload['slides'][number]) : s)),
    }));
  };

  const addSlide = () => {
    setPayload((prev) => ({
      ...prev,
      slides: [
        ...prev.slides,
        { id: `slide_${prev.slides.length + 1}`, kind: 'multiple-choice', prompt: '', options: ['', ''] },
      ],
    }));
    setSlideIndex(payload.slides.length);
  };

  const save = () => {
    try {
      validateActivityPayload('poll', payload);
      setError(null);
      onSaved?.(
        saveActivityDraft({
          id: savedId,
          mode: 'poll',
          title: payload.title,
          subjectTag: subjectTag.trim() || 'general',
          payload,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  };

  const handleAiBuild = async (prompt: string) => {
    setAiLoading(true);
    setError(null);
    setNotice(null);
    try {
      const { draft, notice: aiNotice } = await generateSubjectAwareActivityDraft({
        mode: 'poll',
        subjectId: launchSubjectId,
        subjectTag: subjectTag.trim() || undefined,
        tuning: prompt,
        draftId: savedId,
        persist: false,
      });
      setPayload(draft.payload as PollPayload);
      if (draft.subjectTag) setSubjectTag(draft.subjectTag);
      setSlideIndex(0);
      if (aiNotice) setNotice(aiNotice);
    } catch (e) {
      setError(
        e instanceof Error && e.message === 'subject_context_required'
          ? 'Selecciona una asignatura desde Herramientas para generar con IA.'
          : e instanceof Error
            ? e.message
            : 'No se pudo generar con IA',
      );
    } finally {
      setAiLoading(false);
    }
  };

  const sidebar = slide ? (
    <div className="space-y-3">
      <label className="block">
        <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">Formato</span>
        <select
          className="mt-1 w-full rounded-lg border-0 bg-slate-100/90 px-2 py-1.5 text-xs dark:bg-white/5"
          value={slide.kind}
          onChange={(e) => {
            const kind = e.target.value as PollPayload['slides'][number]['kind'];
            if (kind === 'multiple-choice') updateSlide({ kind, options: ['', ''] });
            else if (kind === 'word-cloud') updateSlide({ kind, maxWordsPerStudent: 3 });
            else if (kind === 'scale') updateSlide({ kind, min: 1, max: 5 });
            else updateSlide({ kind: 'slider', min: 0, max: 100, step: 1 });
          }}
        >
          <option value="multiple-choice">Elección múltiple</option>
          <option value="word-cloud">Nube de palabras</option>
          <option value="scale">Escala</option>
          <option value="slider">Slider</option>
        </select>
      </label>
      {slide.kind === 'scale' && (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number"
            className="rounded-lg border-0 bg-slate-100/90 px-2 py-1 text-xs"
            value={slide.min}
            onChange={(e) => updateSlide({ min: Number(e.target.value) })}
            aria-label="Mínimo"
          />
          <input
            type="number"
            className="rounded-lg border-0 bg-slate-100/90 px-2 py-1 text-xs"
            value={slide.max}
            onChange={(e) => updateSlide({ max: Number(e.target.value) })}
            aria-label="Máximo"
          />
        </div>
      )}
      <button type="button" className="studio-rail-btn w-full" onClick={addSlide}>
        + Momento
      </button>
    </div>
  ) : null;

  if (!slide) return null;

  return (
    <StudioLiveShell
      embedded={embedded}
      error={error}
      subjectTag={subjectTag}
      onSubjectTagChange={setSubjectTag}
      onSave={save}
      aiActivityLabel="encuesta"
      onAiBuild={handleAiBuild}
      aiLoading={aiLoading}
      sidebar={sidebar}
      sidebarTitle="Ajustes"
      sidebarMinimal
    >
      {notice ? (
        <p className="mx-4 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] text-amber-900">
          {notice}
        </p>
      ) : null}
      <header className="plugin-live-toolbar plugin-live-toolbar--ghost">
        <input
          className="canvas-inline-input canvas-inline-input--title min-w-0 flex-1 bg-transparent font-display text-base font-semibold tracking-tight"
          value={payload.title}
          onChange={(e) => setPayload((p) => ({ ...p, title: e.target.value }))}
          placeholder="Título de la actividad"
        />
        <span className="studio-pill">{meta.displayName}</span>
      </header>

      <div className="studio-slide-rail">
        {payload.slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className="studio-slide-rail__tab"
            data-active={i === slideIndex}
            onClick={() => setSlideIndex(i)}
          >
            {i + 1}
          </button>
        ))}
      </div>

      <div className="studio-canvas studio-canvas--poll flex min-h-0 flex-1 flex-col justify-center px-6 py-8 sm:px-10">
        <textarea
          className="canvas-inline-input mb-10 w-full resize-none bg-transparent text-center font-display text-2xl font-semibold leading-snug tracking-tight sm:text-3xl"
          rows={2}
          value={slide.prompt}
          placeholder="Escribe tu pregunta aquí…"
          onChange={(e) => updateSlide({ prompt: e.target.value })}
        />

        {slide.kind === 'multiple-choice' && (
          <div className="mx-auto grid w-full max-w-2xl gap-3 sm:grid-cols-2">
            {slide.options.map((opt, i) => (
              <label
                key={i}
                className="studio-option-card group cursor-text"
                style={{ background: OPTION_TINTS[i % OPTION_TINTS.length] }}
              >
                <span className="studio-option-card__letter">{String.fromCharCode(65 + i)}</span>
                <input
                  className="canvas-inline-input min-w-0 flex-1 bg-transparent font-display text-sm font-semibold tracking-tight"
                  value={opt}
                  placeholder={`Opción ${String.fromCharCode(65 + i)}`}
                  onChange={(e) => {
                    const options = [...slide.options];
                    options[i] = e.target.value;
                    updateSlide({ options });
                  }}
                />
              </label>
            ))}
            <button
              type="button"
              className="studio-option-card studio-option-card--add"
              onClick={() => updateSlide({ options: [...slide.options, ''] })}
            >
              + Opción
            </button>
          </div>
        )}

        {slide.kind === 'word-cloud' && (
          <p className="text-center text-sm font-medium text-slate-400">Los alumnos enviarán palabras en vivo</p>
        )}

        {slide.kind === 'scale' && (
          <div className="mx-auto flex w-full max-w-md justify-between px-2 text-sm font-semibold text-slate-500">
            <span>{slide.min}</span>
            <span className="text-slate-300">—</span>
            <span>{slide.max}</span>
          </div>
        )}

        {slide.kind === 'slider' && (
          <div className="mx-auto w-full max-w-md opacity-40">
            <input type="range" className="w-full accent-violet-400" min={slide.min} max={slide.max} disabled />
          </div>
        )}
      </div>
    </StudioLiveShell>
  );
}
